import { useEffect, useState, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Search, Plus, Play, Trash2, Edit2, X, Bell, MapPin,
  DollarSign, Home, Building, Clock, ChevronDown,
} from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function formatDate(d) {
  if (!d) return 'Never';
  return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}

function SearchFormModal({ initial, onSave, onClose }) {
  const [form, setForm] = useState(initial || {
    name: '', city: '', state_code: '', postal_code: '',
    min_price: '', max_price: '', beds_min: '', baths_min: '',
    property_type: '', status_filter: 'for_sale',
  });
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const handleSubmit = (e) => {
    e.preventDefault();
    const payload = { ...form };
    ['min_price', 'max_price', 'beds_min', 'baths_min'].forEach(k => {
      payload[k] = payload[k] ? parseInt(payload[k], 10) : null;
    });
    ['city', 'state_code', 'postal_code', 'property_type'].forEach(k => {
      if (!payload[k]) payload[k] = null;
    });
    onSave(payload);
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center" onClick={onClose} data-testid="search-form-modal">
      <div className="bg-card border border-border rounded-xl w-full max-w-md mx-4 p-5" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-base font-semibold text-foreground">{initial ? 'Edit' : 'New'} Saved Search</h2>
          <button onClick={onClose} className="p-1 rounded-lg hover:bg-muted text-muted-foreground" data-testid="close-search-form"><X size={16} /></button>
        </div>
        <form onSubmit={handleSubmit} className="space-y-3">
          <div>
            <Label className="text-xs text-muted-foreground">Name</Label>
            <Input value={form.name} onChange={e => set('name', e.target.value)} required placeholder="3BR Sacramento under 500k" className="bg-muted border-border text-foreground text-sm h-9" data-testid="search-name-input" />
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            <div>
              <Label className="text-xs text-muted-foreground">City</Label>
              <Input value={form.city} onChange={e => set('city', e.target.value)} placeholder="Sacramento" className="bg-muted border-border text-foreground text-sm h-9" data-testid="search-city-input" />
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">State</Label>
              <Input value={form.state_code} onChange={e => set('state_code', e.target.value)} placeholder="CA" className="bg-muted border-border text-foreground text-sm h-9" data-testid="search-state-input" />
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Zip</Label>
              <Input value={form.postal_code} onChange={e => set('postal_code', e.target.value)} placeholder="95816" className="bg-muted border-border text-foreground text-sm h-9" data-testid="search-zip-input" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-xs text-muted-foreground">Min Price</Label>
              <Input type="number" value={form.min_price} onChange={e => set('min_price', e.target.value)} placeholder="200000" className="bg-muted border-border text-foreground text-sm h-9" data-testid="search-min-price" />
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Max Price</Label>
              <Input type="number" value={form.max_price} onChange={e => set('max_price', e.target.value)} placeholder="600000" className="bg-muted border-border text-foreground text-sm h-9" data-testid="search-max-price" />
            </div>
          </div>
          <div className="grid grid-cols-3 gap-2">
            <div>
              <Label className="text-xs text-muted-foreground">Beds</Label>
              <Input type="number" value={form.beds_min} onChange={e => set('beds_min', e.target.value)} placeholder="3" className="bg-muted border-border text-foreground text-sm h-9" data-testid="search-beds-input" />
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Baths</Label>
              <Input type="number" value={form.baths_min} onChange={e => set('baths_min', e.target.value)} placeholder="2" className="bg-muted border-border text-foreground text-sm h-9" data-testid="search-baths-input" />
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Type</Label>
              <Input value={form.property_type} onChange={e => set('property_type', e.target.value)} placeholder="single_family" className="bg-muted border-border text-foreground text-sm h-9" data-testid="search-type-input" />
            </div>
          </div>
          <Button type="submit" className="w-full bg-emerald-600 hover:bg-emerald-700 text-foreground h-9 text-sm" data-testid="save-search-btn">
            {initial ? 'Update' : 'Create'} Search
          </Button>
        </form>
      </div>
    </div>
  );
}

function RunResultsPanel({ result, onClose }) {
  if (!result) return null;
  const listings = result.results || [];
  const alerts = result.alerts || [];

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex justify-end" onClick={onClose} data-testid="run-results-overlay">
      <div className="w-full max-w-lg bg-card border-l border-border h-full overflow-y-auto animate-in slide-in-from-right" onClick={e => e.stopPropagation()} data-testid="run-results-panel">
        <div className="sticky top-0 bg-card/95 backdrop-blur border-b border-border p-4 flex items-center justify-between z-10">
          <div>
            <h2 className="text-base font-semibold text-foreground">Search Results</h2>
            <p className="text-xs text-muted-foreground mt-0.5">{result.total_results} total &middot; {alerts.length} alert{alerts.length !== 1 ? 's' : ''}</p>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-muted text-muted-foreground" data-testid="close-run-results"><X size={18} /></button>
        </div>
        <div className="p-4 space-y-4">
          {alerts.length > 0 && (
            <div>
              <h3 className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-2">Alerts</h3>
              {alerts.map((a, i) => (
                <div key={i} className="bg-amber-500/5 border border-amber-500/10 rounded-lg p-3 mb-2">
                  <Badge className="text-[10px] bg-amber-500/10 text-amber-400 border-amber-500/20 mb-1">{a.alert_type}</Badge>
                  <p className="text-sm text-foreground">{a.listing_summary}</p>
                </div>
              ))}
            </div>
          )}
          <div>
            <h3 className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-2">Listings ({listings.length})</h3>
            {listings.map((li, i) => (
              <div key={i} className="bg-muted rounded-lg p-3 mb-2" data-testid={`listing-${i}`}>
                <div className="flex items-center justify-between mb-1">
                  <p className="text-sm font-medium text-foreground">{li.price ? `$${li.price.toLocaleString()}` : 'N/A'}</p>
                  <span className="text-[10px] text-muted-foreground">{li.property_type?.replace(/_/g, ' ')}</span>
                </div>
                <p className="text-xs text-muted-foreground">{li.address_line}, {li.city} {li.state}</p>
                <p className="text-xs text-muted-foreground mt-0.5">{li.beds}bd / {li.baths}ba / {li.sqft?.toLocaleString()} sqft</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function SavedSearchesPage() {
  const { token } = useAuth();
  const [searches, setSearches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editSearch, setEditSearch] = useState(null);
  const [runResult, setRunResult] = useState(null);
  const [running, setRunning] = useState(null);
  const [alertCount, setAlertCount] = useState(0);

  const hdrs = useCallback(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const fetchData = useCallback(async () => {
    try {
      const [searchRes, alertRes] = await Promise.all([
        axios.get(`${API}/saved-searches/`, { headers: hdrs() }),
        axios.get(`${API}/saved-searches/alerts/unread-count`, { headers: hdrs() }),
      ]);
      setSearches(searchRes.data);
      setAlertCount(alertRes.data.unread || 0);
    } catch (err) {
      console.error('Failed to fetch saved searches:', err);
    } finally {
      setLoading(false);
    }
  }, [hdrs]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleCreate = async (data) => {
    try {
      await axios.post(`${API}/saved-searches/`, data, { headers: hdrs() });
      setShowForm(false);
      fetchData();
    } catch (err) {
      console.error('Create failed:', err);
    }
  };

  const handleUpdate = async (data) => {
    try {
      await axios.put(`${API}/saved-searches/${editSearch.id}`, data, { headers: hdrs() });
      setEditSearch(null);
      fetchData();
    } catch (err) {
      console.error('Update failed:', err);
    }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/saved-searches/${id}`, { headers: hdrs() });
      fetchData();
    } catch (err) {
      console.error('Delete failed:', err);
    }
  };

  const handleRun = async (id) => {
    setRunning(id);
    try {
      const { data } = await axios.post(`${API}/saved-searches/${id}/run`, {}, { headers: hdrs() });
      setRunResult(data);
      fetchData(); // Refresh to update last_run_at
    } catch (err) {
      console.error('Run failed:', err);
    } finally {
      setRunning(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-emerald-500 border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-6xl mx-auto space-y-5 sm:space-y-6" data-testid="saved-searches-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-foreground">Saved Searches</h1>
          <p className="text-xs sm:text-sm text-muted-foreground mt-1">
            {searches.length} search{searches.length !== 1 ? 'es' : ''}
            {alertCount > 0 && <span className="text-amber-400 ml-2">&middot; {alertCount} unread alert{alertCount !== 1 ? 's' : ''}</span>}
          </p>
        </div>
        <Button onClick={() => setShowForm(true)} className="bg-emerald-600 hover:bg-emerald-700 text-foreground h-9 text-sm gap-1.5 w-full sm:w-auto" data-testid="new-search-btn">
          <Plus size={14} /> New Search
        </Button>
      </div>

      {searches.length === 0 ? (
        <Card className="border-border bg-card">
          <CardContent className="py-12 text-center">
            <Search className="h-8 w-8 text-muted-foreground mx-auto mb-3" />
            <p className="text-sm text-muted-foreground">No saved searches yet</p>
            <p className="text-xs text-muted-foreground mt-1">Create one manually or let the AI assistant create them from buyer conversations</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {searches.map(s => (
            <Card key={s.id} className="border-border bg-card" data-testid={`search-card-${s.id}`}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <h3 className="text-sm font-medium text-foreground">{s.name}</h3>
                    <Badge className="text-[10px] bg-muted text-muted-foreground mt-1">{s.created_from}</Badge>
                  </div>
                  <div className="flex items-center gap-1">
                    <button onClick={() => setEditSearch(s)} className="p-1.5 rounded-lg hover:bg-muted text-muted-foreground hover:text-foreground transition-colors" data-testid={`edit-search-${s.id}`}>
                      <Edit2 size={14} />
                    </button>
                    <button onClick={() => handleDelete(s.id)} className="p-1.5 rounded-lg hover:bg-muted text-muted-foreground hover:text-red-400 transition-colors" data-testid={`delete-search-${s.id}`}>
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>

                {/* Criteria pills */}
                <div className="flex flex-wrap gap-1.5 mb-3">
                  {s.city && <CritPill icon={<MapPin size={10} />} text={s.city} />}
                  {s.postal_code && <CritPill icon={<MapPin size={10} />} text={s.postal_code} />}
                  {s.max_price && <CritPill icon={<DollarSign size={10} />} text={`$${(s.max_price / 1000).toFixed(0)}k`} />}
                  {s.beds_min && <CritPill icon={<Home size={10} />} text={`${s.beds_min}+ beds`} />}
                  {s.baths_min && <CritPill icon={<Home size={10} />} text={`${s.baths_min}+ baths`} />}
                  {s.property_type && <CritPill icon={<Building size={10} />} text={s.property_type.replace(/_/g, ' ')} />}
                </div>

                {/* Stats row */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3 text-xs text-muted-foreground">
                    <span>{s.last_result_count} results</span>
                    <span className="flex items-center gap-1"><Clock size={10} /> {formatDate(s.last_run_at)}</span>
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleRun(s.id)}
                    disabled={running === s.id}
                    className="h-7 text-xs border-border text-foreground hover:text-foreground hover:bg-muted gap-1"
                    data-testid={`run-search-${s.id}`}
                  >
                    <Play size={12} /> {running === s.id ? 'Running...' : 'Run'}
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {showForm && <SearchFormModal onSave={handleCreate} onClose={() => setShowForm(false)} />}
      {editSearch && <SearchFormModal initial={editSearch} onSave={handleUpdate} onClose={() => setEditSearch(null)} />}
      {runResult && <RunResultsPanel result={runResult} onClose={() => setRunResult(null)} />}
    </div>
  );
}

function CritPill({ icon, text }) {
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-muted/70 text-[10px] text-muted-foreground border border-border">
      {icon}{text}
    </span>
  );
}
